import React, { useState } from "react";
import {
  Send,
  Download,
  CheckCircle,
  XCircle,
  MessageSquare,
  BarChart3,
  Eye,
  Trash2,
  Users,
  X,
  Calendar,
  Smartphone,
  TrendingUp,
  Clock,
  AlertTriangle,
  PlayCircle,
  RefreshCw,
} from "lucide-react";
import {
  EMPTY_CAMPAIGN_STATS,
  getStatusBadge,
  getStatusTooltipContent,
  canResumeCampaign,
} from "../../utils/campaignList";

/**
 * Single campaign card for the grid — reads only `campaign.stats` numbers.
 */
const CampaignCard = ({
  campaign,
  onRetarget,
  onViewAnalytics,
  onAbort,
  onResume,
  onDelete,
  onDownload,
  onUpdateSchedule,
  onCancelScheduled,
  onReschedule,
  onChangeAccount,
  onRefreshStats,
}) => {
  const [refreshingStats, setRefreshingStats] = useState(false);
  const statusBadge = getStatusBadge(campaign.status);
  const stats = campaign.stats || EMPTY_CAMPAIGN_STATS;
  const showResume = canResumeCampaign(campaign.status);
  const isPreparing = String(campaign.status || "").toLowerCase() === "preparing";
  const prepPercent =
    campaign.preparation_percent != null
      ? campaign.preparation_percent
      : isPreparing && campaign.preparation_total > 0
        ? Math.min(
            100,
            Math.floor(
              ((Number(campaign.prepared_count) || 0) / Number(campaign.preparation_total)) * 100
            )
          )
        : null;

  const handleRefreshStats = async () => {
    if (!onRefreshStats || refreshingStats) return;
    setRefreshingStats(true);
    try {
      await onRefreshStats(campaign.campaign_id);
    } finally {
      setRefreshingStats(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-gray-200 bg-[#e9eeea]">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm font-bold text-gray-900 truncate flex-1">
            {campaign.campaign_name}
          </h3>
          <span
            title={getStatusTooltipContent(campaign)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border flex-shrink-0 cursor-default ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusBadge.dot}`}></span>
            <span className="select-none">{String(campaign.status || "").toUpperCase()}</span>
          </span>
        </div>
        <div className="space-y-1">
          {campaign.segment_name && (
            <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
              <Users size={12} />
              {campaign.segment_name}
            </p>
          )}
          <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
            <MessageSquare size={12} />
            {campaign.template_name || "N/A"}
          </p>
          {campaign.phone_number && (
            <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
              <Smartphone size={12} />
              {campaign.phone_number}
            </p>
          )}
        </div>
      </div>

      {isPreparing && (
        <div className="px-4 py-3 border-b border-amber-100 bg-amber-50">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
              <RefreshCw size={12} className="animate-spin" />
              Preparing audience…
            </p>
            <span className="text-xs font-bold text-amber-800">
              {(campaign.prepared_count || 0).toLocaleString()}
              {campaign.preparation_total != null
                ? ` / ${Number(campaign.preparation_total).toLocaleString()}`
                : ""}
            </span>
          </div>
          <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all duration-500 ease-out"
              style={{ width: `${prepPercent != null ? prepPercent : 8}%` }}
            />
          </div>
          {prepPercent != null && (
            <p className="text-[11px] text-amber-700 mt-1">{prepPercent}% complete</p>
          )}
        </div>
      )}

      <div className="p-4 bg-white">
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button
            type="button"
            onClick={() => onRetarget(campaign, "sent")}
            className="text-center p-2.5 bg-blue-50 rounded-xl border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors cursor-pointer"
            title="Retarget sent contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              <Send size={11} className="text-blue-600" />
              <p className="text-xs text-blue-700 font-bold">Sent</p>
            </div>
            <p className="text-base font-bold text-blue-900">{stats.sent}</p>
          </button>
          <button
            type="button"
            onClick={() => onRetarget(campaign, "delivered")}
            className="text-center p-2.5 bg-emerald-50 rounded-xl border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
            title="Retarget delivered contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle size={11} className="text-emerald-600" />
              <p className="text-xs text-emerald-700 font-bold">Delivered</p>
            </div>
            <p className="text-base font-bold text-emerald-900">{stats.delivered}</p>
          </button>
          <button
            type="button"
            onClick={() => onRetarget(campaign, "failed")}
            className="text-center p-2.5 bg-red-50 rounded-xl border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors cursor-pointer"
            title="Retarget failed contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              <XCircle size={11} className="text-red-600" />
              <p className="text-xs text-red-700 font-bold">Failed</p>
            </div>
            <p className="text-base font-bold text-red-900">{stats.failed}</p>
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-3">
          <button
            type="button"
            onClick={() => onRetarget(campaign, "read")}
            className="text-center p-2 bg-purple-50 rounded-xl border border-purple-200 hover:bg-purple-100 hover:border-purple-300 transition-colors cursor-pointer"
            title="Retarget read contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Eye size={10} className="text-purple-600" />
              <p className="text-xs text-purple-700 font-bold">Read</p>
            </div>
            <p className="text-sm font-bold text-purple-900">{stats.read}</p>
          </button>
          <div className="text-center p-2 bg-orange-50 rounded-xl border border-orange-200">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <MessageSquare size={10} className="text-orange-600" />
              <p className="text-xs text-orange-700 font-bold">Replies</p>
            </div>
            <p className="text-sm font-bold text-orange-900">{stats.replies}</p>
          </div>
          <button
            type="button"
            onClick={() => onRetarget(campaign, "pending")}
            className="text-center p-2 bg-yellow-50 rounded-xl border border-yellow-200 hover:bg-yellow-100 hover:border-yellow-300 transition-colors cursor-pointer"
            title="Retarget pending contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Clock size={10} className="text-yellow-600" />
              <p className="text-xs text-yellow-700 font-bold">Pending</p>
            </div>
            <p className="text-sm font-bold text-yellow-900">{stats.pending}</p>
          </button>
          <button
            type="button"
            onClick={() => onRetarget(campaign, "all")}
            className="text-center p-2 bg-gray-100 rounded-xl border border-gray-200 hover:bg-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
            title="Retarget all contacts"
          >
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Users size={10} className="text-gray-700" />
              <p className="text-xs text-gray-700 font-bold">Total</p>
            </div>
            <p className="text-sm font-bold text-gray-900">{stats.total}</p>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1 font-medium">Success</p>
            <div className="flex items-center justify-center gap-1">
              <TrendingUp size={11} className="text-emerald-600" />
              <p className="text-sm font-bold text-emerald-600">{stats.success_pct.toFixed(1)}%</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1 font-medium">Delivery</p>
            <div className="flex items-center justify-center gap-1">
              <TrendingUp size={11} className="text-blue-600" />
              <p className="text-sm font-bold text-blue-600">{stats.delivery_pct.toFixed(1)}%</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1 font-medium">Failure</p>
            <div className="flex items-center justify-center gap-1">
              <AlertTriangle size={11} className="text-red-600" />
              <p className="text-sm font-bold text-red-600">{stats.failure_pct.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        {(campaign.scheduled_time_original || campaign.scheduled_at) && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5 mb-3">
            <p className="text-xs text-purple-900 flex items-center gap-1.5 font-medium">
              <Calendar size={12} />
              <span className="truncate">
                {campaign.scheduled_time_original ||
                  new Date(campaign.scheduled_at).toLocaleString()}
                {campaign.timezone && (
                  <span className="ml-1 text-purple-700">({campaign.timezone})</span>
                )}
              </span>
            </p>
          </div>
        )}

        {stats.pending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-3">
            <p className="text-xs text-amber-900 flex items-center gap-1.5 font-medium">
              <Clock size={12} />
              <span>Pending: {stats.pending}</span>
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-200">
          <div className="text-xs text-gray-600 font-medium">
            {new Date(campaign.created_at).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleRefreshStats}
              disabled={refreshingStats}
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh numbers"
            >
              <RefreshCw size={14} className={refreshingStats ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={() => onViewAnalytics(campaign)}
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="View Analytics"
            >
              <BarChart3 size={14} />
            </button>
            {campaign.status === "active" && (
              <>
                <button
                  type="button"
                  onClick={() => onAbort(campaign)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Abort"
                >
                  <X size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onDownload(campaign.campaign_id, campaign.campaign_name)}
                  className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download size={14} />
                </button>
              </>
            )}
            {showResume && (
              <>
                <button
                  type="button"
                  onClick={() => onResume(campaign.campaign_id)}
                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  title="Resume Campaign"
                >
                  <PlayCircle size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(campaign.campaign_id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
            {!showResume && campaign.status !== "active" && (
              <>
                <button
                  type="button"
                  onClick={() => onDownload(campaign.campaign_id, campaign.campaign_name)}
                  className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(campaign.campaign_id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        {(campaign.status === "scheduled" ||
          ((campaign.status === "cancelled" || campaign.status === "aborted") &&
            campaign.scheduled_time_original) ||
          campaign.status === "pending") && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
            {campaign.status === "scheduled" && (
              <>
                <button
                  type="button"
                  onClick={() => onUpdateSchedule(campaign)}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 border border-purple-200 transition-colors"
                >
                  <Calendar size={12} />
                  Update Schedule
                </button>
                <button
                  type="button"
                  onClick={() => onCancelScheduled(campaign.campaign_id)}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-red-700 bg-red-50 rounded-xl hover:bg-red-100 border border-red-200 transition-colors"
                >
                  <X size={12} />
                  Cancel
                </button>
              </>
            )}
            {(campaign.status === "cancelled" || campaign.status === "aborted") &&
              campaign.scheduled_time_original && (
                <button
                  type="button"
                  onClick={() => onReschedule(campaign)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 border border-emerald-200 transition-colors"
                >
                  <Calendar size={12} />
                  Reschedule
                </button>
              )}
            {(campaign.status === "scheduled" ||
              campaign.status === "cancelled" ||
              campaign.status === "aborted" ||
              campaign.status === "pending") && (
              <button
                type="button"
                onClick={() => onChangeAccount(campaign)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 border border-blue-200 transition-colors"
              >
                <Smartphone size={12} />
                Change Account
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignCard;
